import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { GetAllCampainData } from '../Redux/Actions/Campaign_Actions';
import { CampaignTable } from '../Component/Campaign Component/CampaignTable';
import { useSidebarWidth } from '../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconFilter   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconCopy     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconPlus     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconLink     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconDollar   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconOpenLink = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
const IconClose    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

/* ─── Link Preview Modal ─────────────────────────────────────────────────── */
const LinkModal = ({ isDark, link, onClose, onCopy }) => {
  if (!link) return null;
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)',
        padding: '20px', animation: 'lm-fadein 0.2s ease both',
      }}
    >
      <style>{`@keyframes lm-fadein{from{opacity:0}to{opacity:1}} @keyframes lm-slidein{from{opacity:0;transform:translateY(20px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
      <div style={{
        width: '100%', maxWidth: '500px',
        background: isDark ? '#1e2132' : '#ffffff',
        borderRadius: '20px', padding: '32px',
        border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
        boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.55)' : '0 20px 60px rgba(0,0,0,0.15)',
        position: 'relative', fontFamily: "'DM Sans', sans-serif",
        animation: 'lm-slidein 0.26s cubic-bezier(0.34,1.15,0.64,1) both',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '14px', right: '14px',
          width: '32px', height: '32px', borderRadius: '10px',
          border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
          background: isDark ? '#252d45' : '#f8fafc',
          color: isDark ? '#9ca3af' : '#64748b',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
        }}>
          <IconClose />
        </button>
        <p style={{ fontSize: '16px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', margin: '0 0 6px' }}>
          Redirect Link
        </p>
        <p style={{ fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', margin: '0 0 20px' }}>
          Full URL for this campaign
        </p>
        <div style={{
          background: isDark ? '#151827' : '#f8fafc',
          border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
          borderRadius: '12px', padding: '14px 16px',
          fontSize: '13px', fontFamily: 'monospace',
          color: isDark ? '#94a3b8' : '#475569',
          wordBreak: 'break-all', lineHeight: 1.6, marginBottom: '20px',
        }}>
          {link}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <CopyToClipboard text={link} onCopy={() => { onCopy(); onClose(); }}>
            <button style={{
              flex: 1, padding: '13px 0', borderRadius: '12px', border: 'none',
              background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)',
              color: '#fff', fontSize: '13px', fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 4px 16px rgba(29,78,216,0.32)',
            }}>
              <IconCopy /> Copy Link
            </button>
          </CopyToClipboard>
          <a href={link} target="_blank" rel="noopener noreferrer" style={{
            flex: 1, padding: '13px 0', borderRadius: '12px',
            border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
            background: 'transparent',
            color: isDark ? '#9ca3af' : '#64748b',
            fontSize: '13px', fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            textDecoration: 'none', fontFamily: "'DM Sans', sans-serif",
          }}>
            <IconOpenLink /> Open Link
          </a>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const CampaignPage = () => {
  const theme        = useTheme();
  const mode         = theme.palette.mode;
  const isDark       = mode === 'dark';
  const dispatch     = useDispatch();
  const sidebarWidth = useSidebarWidth();

  const [loading,      setLoading]      = useState(true);
  const [userlogin,    setUserlogin]    = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [showFilter,   setShowFilter]   = useState(false);
  const [copyOpen,     setCopyOpen]     = useState(false);
  const [copyMsg,      setCopyMsg]      = useState('');
  const [linkModal,    setLinkModal]    = useState('');
  const [page,         setPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  /* ── filter states ── */
  const [filterStatus,    setFilterStatus]    = useState('all');      // all | active | inactive
  const [filterDateFrom,  setFilterDateFrom]  = useState('');
  const [filterDateTo,    setFilterDateTo]    = useState('');
  const [filterLink,      setFilterLink]      = useState('');
  const [filterCostMin,   setFilterCostMin]   = useState('');
  const [filterCostMax,   setFilterCostMax]   = useState('');

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const AllCampaigns_fetch = async () => {
    setLoading(true);
    await dispatch(GetAllCampainData());
    setLoading(false);
  };
  useEffect(() => { AllCampaigns_fetch(); }, []);

  const res          = useSelector((s) => s.AllCampaign.campaign);
  const allCampaigns = Array.isArray(res?.data) ? res.data : [];

  /* ── filtering ── */
  const filtered = allCampaigns.filter((c) => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q || c.CampainName?.toLowerCase().includes(q) || c.redirectLink?.toLowerCase().includes(q);

    const matchStatus =
      filterStatus === 'all'      ? true :
      filterStatus === 'active'   ? c.isactivate === true :
      filterStatus === 'inactive' ? c.isactivate === false : true;

    const createdAt = c.createdAt ? new Date(c.createdAt) : null;
    const matchFrom = !filterDateFrom || (createdAt && createdAt >= new Date(filterDateFrom));
    const matchTo   = !filterDateTo   || (createdAt && createdAt <= new Date(filterDateTo + 'T23:59:59'));

    const matchLink = !filterLink || c.redirectLink?.toLowerCase().includes(filterLink.toLowerCase());

    const cost = parseFloat(c.Cost || 0);
    const matchCostMin = filterCostMin === '' || cost >= parseFloat(filterCostMin);
    const matchCostMax = filterCostMax === '' || cost <= parseFloat(filterCostMax);

    return matchSearch && matchStatus && matchFrom && matchTo && matchLink && matchCostMin && matchCostMax;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* ── stats ── */
  const totalCost   = allCampaigns.reduce((sum, c) => sum + parseFloat(c.Cost || 0), 0);
  const activeCamps = allCampaigns.filter((c) => c.isactivate).length;
  const newestCamp  = allCampaigns.length
    ? allCampaigns.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b)
    : null;

  const homeLink =
    userlogin?.data?.role === 'Admin'      ? '/AllLeadsForAdmin'
  : userlogin?.data?.role === 'Marketer'   ? '/ALLLeadsForMarketer'
  : userlogin?.data?.role === 'Accountant' ? '/LeadsForAccountant'
  : '/Dashboard';

  const handleCopy = (msg = 'Copied to clipboard!') => { setCopyMsg(msg); setCopyOpen(true); };

  const clearFilters = () => {
    setSearchTerm(''); setFilterStatus('all');
    setFilterDateFrom(''); setFilterDateTo('');
    setFilterLink(''); setFilterCostMin(''); setFilterCostMax('');
    setPage(1);
  };

  /* ── page numbers ── */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)               pages.push(1, 2, 3, 4, 5, '...', totalPages);
    else if (page >= totalPages - 3) pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    else pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    return pages;
  };

  /* ── input shared style ── */
  const filterInput = {
    padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
    border: `1px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
    backgroundColor: isDark ? '#151827' : '#f8fafc',
    color: isDark ? '#e5e7eb' : '#1f2937',
    fontFamily: "'DM Sans', sans-serif", outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };
  const filterLabel = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: isDark ? '#6b7280' : '#94a3b8',
    marginBottom: '5px', display: 'block',
  };

  /* ── render ── */
  return (
    <>
      <div
        className={`ap-shell ${mode}`}
        style={{
          marginLeft: sidebarWidth,
          transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
          height: 'calc(100vh - 50px)',
          overflowY: 'auto', overflowX: 'hidden',
          display: 'flex', flexDirection: 'column',
          marginTop: '50px',
        }}
      >

        {/* ── Breadcrumb ── */}
        <section style={{ padding: '20px 28px 0', marginBottom: '-8px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to={homeLink} style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
              Home
            </Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
            <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
              Campaigns
            </span>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="ap-stats" style={{ padding: '24px 28px 0', flexShrink: 0 }}>

          <div className="ap-stat-card light">
            <div>
              <div className="ap-stat-label">TOTAL CAMPAIGNS</div>
              <div className="ap-stat-value">{allCampaigns.length}</div>
              <div className="ap-stat-badge green">↑ {activeCamps} Active</div>
            </div>
            <div className="ap-stat-icon light-icon"><IconLink /></div>
          </div>

          <div className="ap-stat-card light">
            <div>
              <div className="ap-stat-label">TOTAL COST</div>
              <div className="ap-stat-value" style={{ fontSize: '22px' }}>
                ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="ap-stat-badge green">All campaigns</div>
            </div>
            <div className="ap-stat-icon light-icon"><IconDollar /></div>
          </div>

          <div className="ap-stat-card dark-card">
            <div>
              <div className="ap-stat-label white">LATEST CAMPAIGN</div>
              <div className="ap-stat-value white" style={{ fontSize: 18, marginTop: 4 }}>
                {newestCamp?.CampainName || '—'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                <span className="ap-badge-pill">MOST RECENT</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                  {newestCamp ? moment(newestCamp.createdAt).format('MMM DD, YYYY') : ''}
                </span>
              </div>
            </div>
            <div className="ap-stat-icon dark-icon"><IconTrend /></div>
          </div>
        </section>

        {/* ── Table Section ── */}
        <section
          className="ap-table-section"
          style={{ margin: '24px 28px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >

          {/* header */}
          <div className="ap-table-header" style={{ flexShrink: 0 }}>
            <div>
              <div className="ap-table-title-label">MARKETING</div>
              <div className="ap-table-title">CAMPAIGNS</div>
            </div>
            <div className="ap-table-actions">
              <div className="ap-search-inline">
                <IconSearch />
                <input
                  placeholder="Search campaigns…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
              </div>
              <button className="ap-filter-btn" onClick={() => setShowFilter(!showFilter)}>
                <IconFilter /> Filter
              </button>
              <Link to="/AddCampaigns" style={{ textDecoration: 'none' }}>
                <button className="ap-create-btn"><IconPlus /> Add Campaign</button>
              </Link>
            </div>
          </div>

          {/* ── Filter Row ── */}
          {showFilter && (
            <div style={{
              flexShrink: 0,
              padding: '16px 20px',
              backgroundColor: isDark ? '#161929' : '#f8fafc',
              border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
              borderRadius: '12px',
              marginBottom: '12px',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>

                {/* Status */}
                <div>
                  <label style={filterLabel}>Status</label>
                  <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={filterInput}>
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label style={filterLabel}>Created From</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }}
                    style={filterInput}
                  />
                </div>

                {/* Date To */}
                <div>
                  <label style={filterLabel}>Created To</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }}
                    style={filterInput}
                  />
                </div>

                {/* Redirect Link */}
                <div>
                  <label style={filterLabel}>Redirect Link</label>
                  <input
                    type="text"
                    placeholder="Filter by link…"
                    value={filterLink}
                    onChange={(e) => { setFilterLink(e.target.value); setPage(1); }}
                    style={filterInput}
                  />
                </div>

                {/* Cost Min */}
                <div>
                  <label style={filterLabel}>Cost Min ($)</label>
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    value={filterCostMin}
                    onChange={(e) => { setFilterCostMin(e.target.value); setPage(1); }}
                    style={filterInput}
                  />
                </div>

                {/* Cost Max */}
                <div>
                  <label style={filterLabel}>Cost Max ($)</label>
                  <input
                    type="number"
                    placeholder="Any"
                    min="0"
                    value={filterCostMax}
                    onChange={(e) => { setFilterCostMax(e.target.value); setPage(1); }}
                    style={filterInput}
                  />
                </div>

              </div>

              {/* Clear + count */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                <span style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#94a3b8' }}>
                  Showing <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{filtered.length}</strong> of <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{allCampaigns.length}</strong> campaigns
                </span>
                <button
                  className="ap-clear-btn"
                  onClick={clearFilters}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* ── Table ── */}
          <div className="ap-table-wrap" style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
            <table className="ap-table">
              <thead>
                <tr>
                  <th style={{ width: 40, position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>
                    <input type="checkbox" className="ap-checkbox" />
                  </th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>CAMPAIGN NAME</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>REDIRECT LINK</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>COST</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>STATUS</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>CREATED DATE</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#1a1a2e' : '#fff' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="ap-empty">
                      <div className="ap-spinner" />
                      <span>Loading campaigns…</span>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ap-empty">
                      <IconSearch />
                      <p>No campaigns found</p>
                    </td>
                  </tr>
                ) : (
                  pageItems.map((item, i) => (
                    <tr key={item._id || i} className="ap-row">
                      <td><input type="checkbox" className="ap-checkbox" /></td>

                      {/* Name */}
                      <td>
                        <div className="ap-project-cell">
                          <div className="ap-project-thumb">
                            {item.CampainName?.slice(0, 2).toUpperCase() || 'CP'}
                          </div>
                          <span className="ap-project-name">{item.CampainName || '—'}</span>
                        </div>
                      </td>

                      {/* Link */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span
                            style={{
                              fontSize: '13px',
                              color: isDark ? '#60a5fa' : '#2563eb',
                              cursor: 'pointer', maxWidth: '160px',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            onClick={() => item.redirectLink && setLinkModal(item.redirectLink)}
                          >
                            {item.redirectLink
                              ? item.redirectLink.length > 30
                                ? item.redirectLink.slice(0, 30) + '…'
                                : item.redirectLink
                              : '—'}
                          </span>
                          {item.redirectLink && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <CopyToClipboard text={item.redirectLink} onCopy={() => handleCopy('Link copied!')}>
                                <button className="ap-action-btn" title="Copy link"><IconCopy /></button>
                              </CopyToClipboard>
                              <button className="ap-action-btn" title="Preview link" onClick={() => setLinkModal(item.redirectLink)}>
                                <IconOpenLink />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Cost */}
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#4ade80' : '#16a34a' }}>
                          ${parseFloat(item.Cost || 0).toFixed(2)}
                        </span>
                      </td>

                      {/* Status */}
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                          backgroundColor: item.isactivate
                            ? (isDark ? 'rgba(74,222,128,0.12)' : 'rgba(22,163,74,0.08)')
                            : (isDark ? 'rgba(248,113,113,0.12)' : 'rgba(239,68,68,0.08)'),
                          color: item.isactivate
                            ? (isDark ? '#4ade80' : '#16a34a')
                            : (isDark ? '#f87171' : '#ef4444'),
                          border: `1px solid ${item.isactivate
                            ? (isDark ? 'rgba(74,222,128,0.25)' : 'rgba(22,163,74,0.2)')
                            : (isDark ? 'rgba(248,113,113,0.25)' : 'rgba(239,68,68,0.2)')}`,
                        }}>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            backgroundColor: item.isactivate
                              ? (isDark ? '#4ade80' : '#16a34a')
                              : (isDark ? '#f87171' : '#ef4444'),
                          }} />
                          {item.isactivate ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="ap-cell-muted" style={{ fontSize: 13 }}>
                        {item.createdAt ? moment(item.createdAt).format('MMM DD, YYYY') : '—'}
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="ap-actions">
                          <CopyToClipboard text={item._id} onCopy={() => handleCopy('ID copied!')}>
                            <button className="ap-action-btn" title="Copy ID"><IconCopy /></button>
                          </CopyToClipboard>
                          <CampaignTable campaign={item} AllCampaigns={AllCampaigns_fetch} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination — sticky أسفل الجدول زي Developers ── */}
          {filtered.length > 0 && (
            <div
              className="ap-pagination"
              style={{
                position: 'sticky', bottom: 0, zIndex: 5, flexShrink: 0,
                backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
                borderTop: `1px solid ${isDark ? '#2a2a45' : '#e8eaf0'}`,
                boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
              }}
            >
              <div className="ap-pagination-info">
                <span>Items per page:</span>
                <select
                  className="ap-page-select"
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>
                  Showing{' '}
                  <strong>{(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, filtered.length)}</strong>
                  {' '}of{' '}
                  <strong>{filtered.length}</strong> campaigns
                </span>
              </div>

              <div className="ap-pagination-btns">
                <button className="ap-pag-btn" onClick={() => setPage(1)} disabled={page === 1}>⟨|</button>
                <button className="ap-pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>

                {buildPageNumbers().map((n, idx) =>
                  n === '...'
                    ? <span key={`e-${idx}`} className="ap-pag-ellipsis">…</span>
                    : <button
                        key={n}
                        className={`ap-pag-btn ${page === n ? 'active' : ''}`}
                        onClick={() => setPage(n)}
                      >{n}</button>
                )}

                <button className="ap-pag-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                <button className="ap-pag-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>|⟩</button>
              </div>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="ap-footer" style={{ padding: '16px 28px', marginTop: 'auto', flexShrink: 0 }}>
          <span>© 2026 Realatix CRM. Architectural & Digital Concierge Excellence.</span>
          <div className="ap-footer-links">
            <span>Privacy Protocol</span>
            <span>Terms of Engagement</span>
          </div>
        </footer>
      </div>

      {/* Link Preview Modal */}
      {linkModal && (
        <LinkModal
          isDark={isDark}
          link={linkModal}
          onClose={() => setLinkModal('')}
          onCopy={() => handleCopy('Link copied!')}
        />
      )}

      {/* Snackbar */}
      <Snackbar open={copyOpen} autoHideDuration={2000} onClose={() => setCopyOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" sx={{ width: '100%' }}>{copyMsg}</Alert>
      </Snackbar>
    </>
  );
};
